import React from "react";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/SectionDivider";
import { ArrowRight, ChevronDown } from "lucide-react";

const STEPS = [
  {
    label: "Indian Bank Account",
    location: "INDIA",
    body: "Your existing bank account in India — the starting point.",
  },
  {
    label: "Citibank Virtual Account",
    location: "USA",
    body: "USD account in your own name, funded via the RBI's LRS.",
  },
  {
    label: "Interactive Brokers",
    location: "USA",
    body: "Global brokerage in your own name, actively managed.",
  },
];

const HowItWorks = () => {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="relative bg-[#080808] py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Eyebrow testId="how-eyebrow">How It Works</Eyebrow>
          <h2
            data-testid="how-heading"
            className="font-serif-display text-[#C9960C] text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]"
          >
            The Global Vault Structure
          </h2>
          <p className="mt-6 max-w-2xl text-[#F8F6F0]/70 text-base sm:text-lg leading-relaxed">
            Three accounts. One client. Held in your name, regulated in their respective jurisdictions, connected through legal channels.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr_auto_1fr] gap-6 lg:gap-4 items-stretch">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.label}>
              <Reveal delay={idx * 0.15}>
                <div
                  data-testid={`flow-step-${idx + 1}`}
                  className="bg-[#1C1C1C] border border-[#C9960C]/50 border-t-2 border-t-[#C9960C] p-8 h-full flex flex-col"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="font-serif-display text-[#C9960C] text-2xl">0{idx + 1}</span>
                    <span className="text-[10px] tracking-[0.32em] uppercase text-[#F5D485]/70 border border-[#C9960C]/40 px-2 py-1">
                      {s.location}
                    </span>
                  </div>
                  <h3 className="font-serif-display text-[#F5D485] text-2xl mb-3">{s.label}</h3>
                  <p className="text-[#F8F6F0]/75 text-[15px] leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
              {idx < STEPS.length - 1 && (
                <div
                  className="flex items-center justify-center text-[#C9960C]"
                  data-testid={`flow-arrow-${idx + 1}`}
                  aria-hidden="true"
                >
                  <ArrowRight size={28} className="hidden lg:block" />
                  <ChevronDown size={28} className="lg:hidden" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        <Reveal delay={0.4}>
          <div
            data-testid="nag-panel"
            className="mt-12 relative bg-gradient-to-b from-[#1C1C1C] to-[#141414] border border-[#C9960C] border-t-2 p-10 md:p-12"
          >
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F5D485] to-transparent" />
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="md:w-1/3">
                <p className="text-[11px] tracking-[0.32em] uppercase text-[#C9960C] mb-2">
                  The Engine
                </p>
                <h3 className="font-serif-display text-[#F5D485] text-3xl sm:text-4xl tracking-tight">
                  NAG ANALYTIX
                </h3>
              </div>
              <div className="md:flex-1 md:border-l md:border-[#C9960C]/30 md:pl-10">
                <p className="text-[#F8F6F0]/85 text-lg leading-relaxed">
                  Actively manages your global portfolio — investing exclusively in <span className="text-[#F5D485] italic">Global Monopolies</span>: structurally unassailable businesses with worldwide pricing power.
                </p>
              </div>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.5}>
          <div
            data-testid="holding-exit-panel"
            className="mt-10 bg-[#1C1C1C] border border-[#C9960C]/40 border-t-2 border-t-[#C9960C] p-10 md:p-12"
          >
            <div className="flex flex-col md:flex-row md:items-start gap-8 md:gap-12">
              <div className="md:w-1/3">
                <p className="text-[11px] tracking-[0.32em] uppercase text-[#C9960C] mb-3">
                  On Your Terms
                </p>
                <h3 className="font-serif-display text-[#F5D485] text-3xl sm:text-4xl tracking-tight leading-tight">
                  Holding Period &amp; Exit
                </h3>
              </div>
              <div className="md:flex-1 md:border-l md:border-[#C9960C]/30 md:pl-12 space-y-5">
                <p className="text-[#F8F6F0]/85 text-lg leading-relaxed">
                  There is <span className="text-[#F5D485]">no fixed holding period</span>. Your capital is never locked — you decide when to stay invested and when to exit.
                </p>
                <p className="text-[#F8F6F0]/80 leading-relaxed">
                  When you choose to exit, funds are transferred to a bank account held in your own name and credited within <span className="text-[#F5D485]">2 to 3 business days</span>.
                </p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default HowItWorks;
